/**
 * Commodity Channel Index (CCI)
 *
 * Oscillator measuring the current price level relative to an average
 * over `period` bars.  Typical price = (H + L + C) / 3.
 * Default period: 20.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";

export const cciIndicator: IndicatorDefinition = {
  id: "cci",
  name: "Commodity Channel Index",
  shortName: "CCI",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 300 },
  ],
  plots: [
    { key: "cci", label: "CCI", color: "#009688", style: "line", lineWidth: 2, priceScaleId: "cci" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const tp: number[] = candles.map(c => (c.high + c.low + c.close) / 3);
    const tpSMA = computeSMA(tp, period);
    const data: { time: number; value: number }[] = [];

    for (let i = period - 1; i < tp.length; i++) {
      const mean = tpSMA[i];
      if (isNaN(mean)) continue;

      // Mean deviation
      let mdSum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        mdSum += Math.abs(tp[j] - mean);
      }
      const md = mdSum / period;
      const cci = md === 0 ? 0 : (tp[i] - mean) / (0.015 * md);
      data.push({ time: candles[i].time, value: cci });
    }

    return [{ key: "cci", data }];
  },
};
